function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import { FileInput } from '@leverege/ui-elements';
import Quagga from '@ericblade/quagga2';
import jsQR from 'jsqr';

/**
 * A component that allows the user to input mixed codes (QR codes and barcodes) by uploading an image file.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.eventData - The event data to be passed to the `onChange` function.
 * @param {Function} props.onChange - The function to be called when the input value changes.
 * @param {Function} props.onError - The function to be called when an error occurs.
 * @param {string} props.fileTypes - The allowed file types for the uploaded image file.
 * @param {ReactNode} props.children - The child elements to be rendered.
 * @param {string} props.inversionAttempts - The inversion attempts to be used when decoding the image file.
 * @returns {JSX.Element} The `MixedCodeInput` component.
 * @param {...any} props.rest - Any other props to be passed to the `FileInput` component.
 */

function MixedCodeInput({
  eventData,
  onChange,
  onError,
  fileTypes,
  children,
  inversionAttempts = 'attemptBoth',
  ...rest
}) {
  const handleChange = async event => {
    const file = event.value?.[0];
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = async readEvent => {
        const image = new Image();
        image.src = readEvent.target?.result?.toString() || '';
        image.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = image.width;
          canvas.height = image.height;
          const context = canvas.getContext('2d');
          if (context) {
            context.drawImage(image, 0, 0, image.width, image.height);
            const imageDataArray = context.getImageData(0, 0, canvas.width, canvas.height).data;
            const qrCodeResult = jsQR(imageDataArray, image.width, image.height, {
              inversionAttempts
            });
            if (qrCodeResult) {
              onChange({
                data: eventData,
                value: [qrCodeResult].map(q => ({
                  ...q,
                  text: q.data
                }))
              });
            } else {
              Quagga.decodeSingle({
                src: readEvent.target?.result?.toString() || '',
                numOfWorkers: 0,
                decoder: {
                  readers: ['code_128_reader', 'ean_reader']
                }
              }, barcodeResult => {
                if (barcodeResult?.codeResult) {
                  onChange({
                    data: eventData,
                    value: [barcodeResult].map(q => ({
                      ...q,
                      text: q.codeResult.code
                    }))
                  });
                } else {
                  onChange({
                    data: eventData,
                    error: 'Error to decode'
                  });
                }
              });
            }
          } else {
            onChange({
              data: eventData,
              error: 'Error to decode'
            });
          }
        };
      };
      fileReader.readAsDataURL(file);
    }
  };
  return /*#__PURE__*/React.createElement(FileInput, _extends({
    onChange: handleChange,
    fileTypes: fileTypes || 'image/*'
  }, rest), children);
}
export default MixedCodeInput;